import request from 'services/axios'

/**
 * «ضمانت‌های من» — every loan the logged-in member has personally guaranteed,
 * across all their funds. Cross-fund by design: standing as a guarantor is a
 * fact about the person, not about one membership.
 */
export const getMyGuarantees = async () => {
    const { data }: { data: IMyGuarantees } = await request.get(
        '/api/v1/moneypools/my-guarantees/'
    )
    return data
}

/**
 * ضامن‌های یک وام — manager-only; the list IS the new state, [] clears it.
 *
 * `suggested` is the borrower's ضامن عضویت when they have one, offered as the
 * picker's first option. It is never auto-attached.
 */
export const getLoanGuarantors = async (
    moneypoolId: string | number,
    loanId: string | number
) => {
    const { data }: { data: ILoanGuarantorsResponse } = await request.get(
        `/api/v1/moneypools/${moneypoolId}/loans/${loanId}/guarantors/`
    )
    return data
}

export const setLoanGuarantors = async (payload: {
    moneypoolId: string | number
    loanId: string | number
    guarantor_ids: Array<number>
}) => {
    const { data } = await request.post(
        `/api/v1/moneypools/${payload.moneypoolId}/loans/${payload.loanId}/guarantors/`,
        { guarantor_ids: payload.guarantor_ids }
    )
    return data
}

/** ضامن‌ها — the fund-wide guarantor graph behind the admin dashboard tab. */
export const getGuarantorGraph = async (moneypoolId: string | number) => {
    const { data }: { data: IGuarantorGraph } = await request.get(
        `/api/v1/moneypools/${moneypoolId}/admin-dashboard/guarantors/`
    )
    return data
}

/* ------------------------------------------------------ «پیگیری از ضامن» */

export type TNudgeTone = 'friendly' | 'serious'
export type TNudgeChannel = 'bale' | 'eitaa' | 'telegram' | 'sms' | 'app'

export interface INudgeGuarantor {
    poolship_id: number
    name: string
    /** a loan guarantor backed THIS money; a membership one only vouched */
    type: 'loan' | 'membership'
    channel: TNudgeChannel
    checked: boolean
}

export interface INudgeSheet {
    subject: 'loan' | 'portion'
    member: { poolship_id: number; name: string }
    late: { count: number; total: number; oldest_days: number }
    tones: TNudgeTone[]
    guarantors: INudgeGuarantor[]
    inform_member: boolean
    history: Array<{
        id: number
        at: string
        days_ago: number
        by: string
        tone: TNudgeTone
    }>
    next_allowed_at: string | null
    can_send: boolean
    reason: '' | 'nothing_late' | 'no_guarantor' | 'too_soon'
}

/** A late loan (`loanId`) or a member behind on حق عضویت (`poolshipId`). */
export type TNudgeTarget =
    | { kind: 'loan'; loanId: string | number }
    | { kind: 'portion'; poolshipId: string | number }

const nudgeUrl = (moneypoolId: string | number, target: TNudgeTarget) =>
    target.kind === 'loan'
        ? `/api/v1/moneypools/${moneypoolId}/loans/${target.loanId}/guarantor-nudge/`
        : `/api/v1/moneypools/${moneypoolId}/poolships/${target.poolshipId}/guarantor-nudge/`

export const getNudgeSheet = async (
    moneypoolId: string | number,
    target: TNudgeTarget
): Promise<INudgeSheet> => {
    const { data }: { data: { results: INudgeSheet } } = await request.get(
        nudgeUrl(moneypoolId, target)
    )
    return data.results
}

export const sendNudge = async (
    moneypoolId: string | number,
    target: TNudgeTarget,
    body: { tone: TNudgeTone; guarantor_ids: number[]; inform_member: boolean }
) => {
    const { data } = await request.post(nudgeUrl(moneypoolId, target), body)
    return data
}
